interface TenantLanguageLike {
  isActive: boolean
  languageCode: string
  sortOrder: number
}

export function resolveDefaultLanguages(
  tenantLanguages: TenantLanguageLike[] | undefined,
): string[] {
  if (!tenantLanguages) {
    return []
  }

  const seen = new Set<string>()

  const resolved = (tenantLanguages ?? [])
    .filter((item) => item.isActive && item.languageCode?.trim())
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => item.languageCode.trim())
    .filter((languageCode) => {
      if (seen.has(languageCode)) {
        return false
      }
      seen.add(languageCode)
      return true
    })

  return resolved
}

/**
 * Builds one translation row per tenant language, keeping any data already entered.
 *
 * Edit forms hydrate from the entity's `localizedData`, which only covers languages that
 * were translated at the time. Languages added to the tenant afterwards would otherwise
 * have no row, so they never get a tab and cannot be filled in. Rows are ordered by the
 * tenant's language order; translations for languages no longer offered by the tenant are
 * appended rather than dropped, so saving cannot silently discard existing content.
 *
 * @param supportedLanguages - Tenant language codes, already ordered by `sortOrder`.
 * @param existing - Rows hydrated from the entity.
 * @param createEmpty - Factory for a blank row of the caller's translation shape.
 */
export function mergeTranslationsForLanguages<T extends { language: string }>(
  supportedLanguages: readonly string[],
  existing: readonly T[],
  createEmpty: (language: string) => T,
): T[] {
  const existingByLanguage = new Map<string, T>()
  for (const row of existing) {
    const language = row.language?.trim()
    if (language && !existingByLanguage.has(language)) {
      existingByLanguage.set(language, row)
    }
  }

  const merged: T[] = []
  const used = new Set<string>()

  for (const language of supportedLanguages) {
    const code = language.trim()
    if (!code || used.has(code)) {
      continue
    }
    used.add(code)
    merged.push(existingByLanguage.get(code) ?? createEmpty(code))
  }

  for (const row of existing) {
    const language = row.language?.trim()
    if (language && !used.has(language)) {
      used.add(language)
      merged.push(row)
    }
  }

  return merged
}

interface TranslationLike {
  language: string
}

export function hasRequiredTranslations<T extends TranslationLike>(
  translations: readonly T[],
  requiredFields: readonly string[],
  requiredLanguages?: readonly string[],
): boolean {
  const rows = requiredLanguages
    ? requiredLanguages.map((language) => translations.find((row) => row.language === language))
    : translations

  return (
    rows.length > 0 &&
    rows.every(
      (row) =>
        row !== undefined &&
        requiredFields.every((field) => {
          const value = (row as TranslationLike & Record<string, unknown>)[field]
          return typeof value === 'string' ? value.trim().length > 0 : Boolean(value)
        }),
    )
  )
}
